import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import { BrandFormShell, FormSection, FormStepper } from '../components/BrandFormShell';
import BrandLoadingScreen from '../components/BrandLoadingScreen';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const inputClass = 'w-full px-5 py-4 rounded-2xl border border-[#d9e5ef] bg-white focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] transition text-base';

const validatePassword = (pwd) => ({
  length: pwd.length >= 8,
  uppercase: /[A-Z]/.test(pwd),
  number: /[0-9]/.test(pwd),
  special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
});

export default function ForgotPasswordPage() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const pwdRules = validatePassword(newPassword);
  const allRulesMet = Object.values(pwdRules).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    setError('');
    setStep('reset');
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allRulesMet) {
      setError('Please make sure your password meets all requirements');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email: email.trim().toLowerCase(),
        otp,
        newPassword,
      });
      setStep('success');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || '';
      if (msg.toLowerCase().includes('expired')) {
        setError('Your reset code has expired. Please start again.');
        setStep('email');
      } else if (msg.toLowerCase().includes('invalid')) {
        setError('Invalid reset code. Please go back and try again.');
        setStep('otp');
      } else {
        setError(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setOtp('');
    } catch {
      setError('Could not resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { key: 'email', label: 'Email' },
    { key: 'otp', label: 'Code' },
    { key: 'reset', label: 'Reset' },
  ];

  return (
    <>
      <SEOHead title="Forgot Password | LiteRent" description="Reset your LiteRent password securely and get back to your dashboard." canonical="https://literent.com.ng/forgot-password" />
      <div className="min-h-screen bg-gradient-to-br from-[#f8fbff] via-white to-[#f5f9ec] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-5 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-2xl">
            <BrandFormShell
              badge="Password reset"
              title={step === 'email' ? 'Reset your LiteRent password' : step === 'otp' ? 'Check your email for the code' : step === 'reset' ? 'Choose a new password' : 'Password reset complete'}
              subtitle={step === 'email' ? 'Enter the email address attached to your LiteRent account and we will send a secure reset code.' : step === 'otp' ? `We sent a 6-digit code to ${email}. Enter it to continue.` : step === 'reset' ? 'Create a strong new password for your LiteRent account.' : 'Your new password is ready. We are taking you back to the login page now.'}
              footer={<p>Remembered your password? <Link to="/login" className="text-[#294c79] font-semibold underline">Return to sign in</Link>.</p>}
            >
              {step !== 'success' ? <FormStepper steps={steps} currentStep={step} /> : null}

              {error ? (
                <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>
              ) : null}

              {step === 'email' && (
                <FormSection title="Start with your email" description="This helps us send the reset code to the right LiteRent account.">
                  <form onSubmit={handleEmailSubmit} className="space-y-5">
                    <label className="block">
                      <span className="block text-sm font-semibold text-[#294c79] mb-2">Email Address</span>
                      <input id="email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} placeholder="you@example.com" className={inputClass} required />
                    </label>
                    <button type="submit" disabled={loading} className={`w-full py-4 px-6 rounded-2xl text-white font-semibold text-lg transition-all shadow-md ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                      {loading ? 'Sending code…' : 'Send Reset Code'}
                    </button>
                  </form>
                </FormSection>
              )}

              {step === 'otp' && (
                <FormSection title="Enter the 6-digit code" description="If you cannot find the code, check your spam folder or request another one.">
                  <form onSubmit={handleOtpSubmit} className="space-y-6 text-center">
                    <div className="mx-auto w-16 h-16 bg-[#e8f2d1] rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-[#4a7a1e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input value={otp} onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }} maxLength={6} placeholder="------" inputMode="numeric" className="w-full max-w-xs mx-auto block px-8 py-5 text-4xl font-mono tracking-[1em] text-center border border-[#d9e5ef] rounded-2xl focus:outline-none focus:ring-4 focus:ring-[#294c79]/10 focus:border-[#294c79] bg-white" required />
                    <button type="submit" disabled={otp.length !== 6} className={`w-full py-4 px-6 rounded-2xl text-white font-semibold text-lg transition-all shadow-md ${otp.length !== 6 ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                      Verify Code
                    </button>
                    <p className="text-sm text-[#1a1a1a]/70">Need another code? <button type="button" onClick={handleResend} disabled={loading} className="text-[#294c79] font-semibold underline disabled:opacity-50">Resend it</button></p>
                  </form>
                </FormSection>
              )}

              {step === 'reset' && (
                <FormSection title="Create a stronger password" description="Use something memorable to you but hard for other people to guess.">
                  <form onSubmit={handleResetSubmit} className="space-y-5">
                    <label className="block">
                      <span className="block text-sm font-semibold text-[#294c79] mb-2">New Password</span>
                      <div className="relative">
                        <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setError(''); }} placeholder="••••••••" className={`${inputClass} pr-16`} required />
                        <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-[#294c79] font-medium">{showPassword ? 'Hide' : 'Show'}</button>
                      </div>
                    </label>
                    <label className="block">
                      <span className="block text-sm font-semibold text-[#294c79] mb-2">Confirm New Password</span>
                      <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }} placeholder="••••••••" className={inputClass} required />
                    </label>

                    <div className="rounded-2xl border border-[#edf2f7] bg-[#fbfcfe] px-4 py-4 text-sm space-y-1.5">
                      <p className="text-[#294c79] font-semibold">Your password should include:</p>
                      {[
                        ['At least 8 characters', pwdRules.length],
                        ['One uppercase letter', pwdRules.uppercase],
                        ['One number', pwdRules.number],
                        ['One special character', pwdRules.special],
                      ].map(([label, ok]) => (
                        <div key={label} className={`flex items-center gap-2 ${ok ? 'text-green-600' : 'text-[#1a1a1a]/55'}`}>
                          <span>{ok ? '✓' : '○'}</span>
                          <span>{label}</span>
                        </div>
                      ))}
                      <div className={`flex items-center gap-2 ${passwordsMatch ? 'text-green-600' : 'text-[#1a1a1a]/55'}`}>
                        <span>{passwordsMatch ? '✓' : '○'}</span>
                        <span>Passwords match</span>
                      </div>
                    </div>

                    <button type="submit" disabled={loading || !allRulesMet || !passwordsMatch} className={`w-full py-4 px-6 rounded-2xl text-white font-semibold text-lg transition-all shadow-md ${loading || !allRulesMet || !passwordsMatch ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                      {loading ? 'Resetting password…' : 'Reset Password'}
                    </button>
                  </form>
                </FormSection>
              )}

              {step === 'success' && (
                <FormSection title="Success" description="Your password has been updated successfully.">
                  <BrandLoadingScreen message="We are redirecting you back to the LiteRent login page." fullScreen={false} compact />
                </FormSection>
              )}
            </BrandFormShell>
          </div>
        </div>
        <footer className="py-6 text-center text-[#1a1a1a]/60 text-sm border-t border-[#afc09e]/30">
          © {new Date().getFullYear()} LiteRent. All rights reserved.
        </footer>
      </div>
    </>
  );
}
